import Icon from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { websiteEnv } from '@/constants/website-env';
import { cn } from '@/lib/utils';
import { isSupportedProductType as isSupported } from '@/util/isSupportedProductType';
import { CheckCircle, Info } from 'react-feather';
import { useFormContext } from 'react-hook-form';
import type { OptionsSelectionsMap } from '../lib/get-optionSelections';
import { useProductFullDetailStore } from '../store/use-product-full-detail-store';
import { ProductRoot } from '../types/product';

export const ShortsReport = ({ showNotifyMeFormDialog }: { showNotifyMeFormDialog: () => void }) => {
  return (
    <div className={cn('w-full', 'text-center', 'mt-4')}>
      <div className={cn('bg-bgtheme', 'w-full', 'leading-[48px]', 'text-msgerror')}>
        <span>Sorry, item out of stock!</span>
      </div>
      <div className={cn('bg-bgdeeptheme', 'leading-[48px]', 'text-white')}>
        <button
          type='button'
          className='w-full font-jost font-medium uppercase'
          onClick={() => {
            showNotifyMeFormDialog();
          }}
        >
          NOTIFY ME
        </button>
      </div>
    </div>
  );
};

export const CartAction = ({
  product,
  isEverythingOutOfStock,
  addToCartStatus,
  isAddingProduct,
  hasCustomizationOptions,
  optionSelections,
  showNotifyMeFormDialog,
  setIsCustomizationsDialogOpen,
  isValid,
}: {
  product: ProductRoot;
  isEverythingOutOfStock: boolean;
  addToCartStatus: boolean;
  isAddingProduct: boolean;
  hasCustomizationOptions: boolean;
  optionSelections: OptionsSelectionsMap;
  showNotifyMeFormDialog: () => void;
  setIsCustomizationsDialogOpen: any;
  isValid: boolean;
  errors?: any;
}) => {
  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const form = useFormContext();

  const { Color, Size, bust, waist, hip, hollowToFloor, height, extraLength, agreement, hollowtoHem } =
    form.watch() || {};

  const setShowMobileCustomlizeDialog = useProductFullDetailStore(state => state.setShowMobileCustomlizeDialog);
  const isOutOfStock = useProductFullDetailStore(state => state.getIsOutOfStock(product, optionSelections));

  const productType = product?.__typename;

  const isSupportedProductType = isSupported(productType);

  // Error message for screen reader
  const productasin = product.show_attribute?.find(item => item.code === 'product_asin')?.value;

  const cartCallToActionText = addToCartStatus ? (
    <Icon src={CheckCircle} />
  ) : isAddingProduct ? (
    <>ADDING...</>
  ) : isEverythingOutOfStock ? (
    <p>Out of Stock</p>
  ) : (
    <p>{websiteEnv.isUnitedKingdom ? 'Add to Bag' : 'Add to Cart'}</p>
  );

  if (isOutOfStock) {
    return <ShortsReport showNotifyMeFormDialog={showNotifyMeFormDialog} />;
  }

  if (isSupportedProductType) {
    return (
      <section className='col-span-2 m-0'>
        {product.show_attribute?.find(item => item.code === 'buy_on_amazon')?.value === 'Yes' ? (
          <a
            href={'https://www.amazon.com/dp/' + productasin + '?tag=aw-bridal-20'}
            className={cn(
              'bg-theme',
              'h-[40px] w-full lg_h-[48px]',
              'rounded-[3px] font-jost font-medium text-white',
              'text-sm',
              'hover_border hover_border-solid hover_border-theme hover_bg-white hover_text-theme',
              'flex items-center justify-center',
              'text-center leading-[46px]',
            )}
          >
            Buy on Amazon
          </a>
        ) : hasCustomizationOptions ? (
          <>
            <Button
              className={cn(
                'bg-theme',
                'h-[40px] w-full lg_h-[48px]',
                'text-white',
                ' rounded-[3px] font-jost text-sm font-medium',
                'hover_border hover_border-solid hover_border-theme hover_bg-white hover_text-theme',
                'flex items-center justify-center',
              )}
              data-cy='ProductFullDetail-customizeButton'
              aria-label={isEverythingOutOfStock ? 'This item is currently out of stock' : ''}
              disabled={isAddingProduct}
              onClick={() => {
                if (!Color) {
                  form.setError('Color', {
                    type: 'required',
                    message: 'This is a required field',
                  });

                  return;
                }

                form.clearErrors('Color'); // 清除Color字段的错误
                setIsCustomizationsDialogOpen(true);
              }}
              type='button'
            >
              Personalize & {websiteEnv.isUnitedKingdom ? 'Add To Bag' : 'Add To Cart'} &gt;
            </Button>
          </>
        ) : (
          <Button
            data-cy='ProductFullDetail-addToCartButton'
            type='submit'
            disabled={isAddingProduct}
            className={cn(
              'h-[45px] w-full lg_h-[48px]',
              ' text-sm',
              'flex items-center justify-center',
              'aw-primary-btn rounded-[3px] font-jost uppercase',
            )}
            onClick={async () => {
              if (Size?.label === 'customized_size') {
                if (!Color) {
                  scrollToElement('Color');
                }

                const isFloorLengthSizeDress =
                  product.show_attribute?.find(item => item.code === 'aw_dresses_hemline')?.value === 'Floor Length';
                const triggerFields = [
                  'bust',
                  'waist',
                  'hip',
                  product?.shipping_type === 'Dresses' ? 'hollowtoHem' : 'hollowToFloor',
                  'height',
                  'agreement',
                ];

                if (isFloorLengthSizeDress) {
                  triggerFields.push('extraLength');
                }

                const isFormValid = await form.trigger(triggerFields);

                if (
                  !bust ||
                  !waist ||
                  !hip ||
                  (product?.shipping_type === 'Dresses' ? !hollowtoHem : !hollowToFloor) ||
                  !height ||
                  (isFloorLengthSizeDress ? !extraLength : false) ||
                  !agreement ||
                  !isFormValid
                ) {
                  setShowMobileCustomlizeDialog(true);
                }
              } else {
                if (!Color) {
                  scrollToElement('Color');

                  return;
                }

                if (!Size) {
                  scrollToElement('Size');
                }
              }
            }}
          >
            {cartCallToActionText}
          </Button>
        )}
      </section>
    );
  }

  return (
    <div
      className={cn(
        'bg-subtle',
        'gap-x-2xs',
        'grid',
        'grid-flow-col',
        'italic',
        'items-center',
        'justify-start',
        'p-3',
        'text-subtle',
        'w-full',
      )}
    >
      <Info />
      <p>This product is currently unavailable for purchase.</p>
    </div>
  );
};
